/**
 * HeroDemoMontage — autoplay looping "demo video" built from real Lumora-generated
 * sample assets + animated UI overlays. Acts as the brief's mandated hero demo
 * without requiring a real .mp4 file.
 *
 * Structure: 3 stages cycling every ~4 seconds with fade transitions:
 *   Stage 1: "Paste your Shopify URL" input animation
 *   Stage 2: "Generating..." with progress bar over a product image
 *   Stage 3: Three finished ad thumbnails (spokesperson / unboxing / testimonial)
 *
 * Pauses on hover. No audio. CDN-backed images so the page stays light.
 */
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Zap, Play, Check } from "lucide-react";

const STAGE_DURATION_MS = 4200;

// "Imported product" thumbnail — appears at end of stage 1 ("we scraped your
// store!"), persists through stage 2, then visually resolves into the 3
// finished ads in stage 3. Stable Unsplash skincare product shot.
const PRODUCT_IMAGE_URL =
  "https://images.unsplash.com/photo-1571781926291-c477ebfd024b?w=240&h=320&fit=crop&q=80&auto=format";

// Sample finished-ad thumbnails — Unsplash CDN portraits in 9:16 crop that
// represent the three most common UGC ad formats. Hotlinking is supported and
// already used elsewhere on the site (see LandingPage testimonials). Swap for
// real Lumora-generated outputs once a curated set exists.
const UNSPLASH_BASE = "?w=600&h=1067&fit=crop&q=80&auto=format";
const FINISHED_ADS = [
  {
    // Beauty spokesperson — skincare creator format
    url: `https://images.unsplash.com/photo-1596462502278-27bfdc403348${UNSPLASH_BASE}`,
    label: "Beauty · Spokesperson",
    fallbackBg: "from-pink-500/40 to-rose-700/40",
  },
  {
    // Tech unboxing — headphones / lifestyle gadget
    url: `https://images.unsplash.com/photo-1583394838336-acd977736f90${UNSPLASH_BASE}`,
    label: "Tech · Unboxing",
    fallbackBg: "from-cyan-500/40 to-blue-700/40",
  },
  {
    // Apparel testimonial — fashion lifestyle portrait
    url: `https://images.unsplash.com/photo-1488426862026-3ee34a7d66df${UNSPLASH_BASE}`,
    label: "Apparel · Testimonial",
    fallbackBg: "from-violet-500/40 to-fuchsia-700/40",
  },
];

const stageVariants = {
  enter: { opacity: 0, scale: 0.96 },
  active: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: "easeOut" } },
  exit: { opacity: 0, scale: 1.02, transition: { duration: 0.35, ease: "easeIn" } },
};

function StageInput() {
  const url = "https://yourstore.com/products/your-product";
  return (
    <motion.div variants={stageVariants} initial="enter" animate="active" exit="exit" className="absolute inset-0 flex items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-md">
        <div className="text-violet-300 text-xs font-bold uppercase tracking-widest mb-3 flex items-center gap-2"><Zap className="w-3.5 h-3.5" /> Step 1 — Paste a URL</div>
        <div className="relative bg-white/[0.05] border border-white/15 rounded-2xl px-5 py-4 overflow-hidden">
          <motion.span
            className="text-white/85 font-mono text-sm"
            initial={{ width: 0 }} animate={{ width: "100%" }} transition={{ duration: 1.4, ease: "easeOut" }}
            style={{ display: "inline-block", whiteSpace: "nowrap", overflow: "hidden" }}
          >
            {url}
          </motion.span>
          <motion.span className="inline-block w-[2px] h-4 bg-violet-300 ml-0.5 align-middle" animate={{ opacity: [1, 0, 1] }} transition={{ duration: 0.8, repeat: Infinity }} />
        </div>

        {/* Imported product card — slides in after URL completes typing */}
        <motion.div
          initial={{ opacity: 0, y: 12, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 1.6, duration: 0.4, ease: "easeOut" }}
          className="mt-3 flex items-center gap-3 rounded-xl bg-violet-500/[0.08] ring-1 ring-violet-400/30 p-3"
          data-testid="hero-demo-product-imported"
          layoutId="lumora-product-image"
        >
          <img
            src={PRODUCT_IMAGE_URL}
            alt="Imported product preview"
            loading="lazy"
            className="w-10 h-12 rounded-md object-cover ring-1 ring-white/10 shrink-0"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
          <div className="flex-1 min-w-0">
            <p className="text-white text-xs font-semibold truncate">Glow Renewal Serum · 30ml</p>
            <p className="text-violet-300 text-[10px] font-medium">$48 USD · 4.8★ (2.1k)</p>
          </div>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 1.95, type: "spring", stiffness: 320, damping: 14 }}
            className="w-6 h-6 rounded-full bg-violet-500 flex items-center justify-center shrink-0"
            data-testid="hero-demo-imported-check"
          >
            <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
          </motion.div>
        </motion.div>

        <div className="mt-3 text-white/70 text-xs">Shopify · Amazon · any storefront — we auto-import title, images &amp; price</div>
      </div>
    </motion.div>
  );
}

function StageGenerating() {
  return (
    <motion.div variants={stageVariants} initial="enter" animate="active" exit="exit" className="absolute inset-0 flex items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-md">
        <div className="text-violet-300 text-xs font-bold uppercase tracking-widest mb-3 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Step 2 — Generating</div>
        <div className="bg-white/[0.05] border border-white/15 rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-4">
            {/* Same product image as stage 1 — provides visual continuity */}
            <motion.img
              src={PRODUCT_IMAGE_URL}
              alt="Source product"
              loading="lazy"
              className="w-12 h-14 rounded-lg object-cover ring-1 ring-white/15 shrink-0"
              layoutId="lumora-product-image"
              data-testid="hero-demo-product-source"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-semibold leading-tight">Spokesperson · Beauty</p>
              <p className="text-white/70 text-xs mt-0.5">9:16 · powered by xAI Grok · ~28s</p>
            </div>
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-500 to-violet-600 flex items-center justify-center animate-spin shrink-0" style={{ animationDuration: "2.5s" }}>
              <Sparkles className="w-4 h-4 text-white" />
            </div>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <motion.div className="h-full bg-gradient-to-r from-violet-400 to-indigo-500" initial={{ width: "5%" }} animate={{ width: "95%" }} transition={{ duration: 3.6, ease: "easeOut" }} />
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function StageFinished() {
  return (
    <motion.div variants={stageVariants} initial="enter" animate="active" exit="exit" className="absolute inset-0 flex flex-col justify-center p-6 md:p-10">
      <div className="text-violet-300 text-xs font-bold uppercase tracking-widest mb-4 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-violet-400 animate-pulse" /> Step 3 — Ready to download
      </div>
      <div className="grid grid-cols-3 gap-2.5">
        {FINISHED_ADS.map((ad, i) => (
          <motion.div
            key={ad.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 + i * 0.12, duration: 0.4 }}
            className="aspect-[9/16] rounded-xl overflow-hidden ring-1 ring-white/15 relative group"
          >
            <div className={`absolute inset-0 bg-gradient-to-br ${ad.fallbackBg}`} aria-hidden="true" />
            <img
              src={ad.url}
              alt={ad.label}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            <div className="absolute bottom-2 left-2 right-2">
              <span className="text-white text-[10px] font-bold uppercase tracking-wider leading-tight block">{ad.label}</span>
            </div>
            <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center ring-1 ring-white/30">
              <Play className="w-3 h-3 text-white fill-white" />
            </div>
          </motion.div>
        ))}
      </div>
      <p className="text-white/75 text-xs mt-4 text-center">~30s total · Free for your first ad</p>
    </motion.div>
  );
}

const STAGES = [StageInput, StageGenerating, StageFinished];

export function HeroDemoMontage() {
  const [stage, setStage] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setTimeout(() => setStage((s) => (s + 1) % STAGES.length), STAGE_DURATION_MS);
    return () => clearTimeout(t);
  }, [stage, paused]);

  const CurrentStage = STAGES[stage];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
      className="relative w-full aspect-[5/6] md:aspect-[4/5] max-w-md mx-auto rounded-3xl overflow-hidden bg-gradient-to-br from-[#0a0420] via-[#10072a] to-[#04010d] ring-1 ring-white/10 shadow-2xl shadow-violet-900/30"
      data-testid="hero-demo-montage"
      role="img"
      aria-label="Lumora AI workflow demonstration — paste URL, generate, download ad"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Subtle ambient gradient */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(167,139,250,0.15),transparent_55%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_70%,rgba(52,211,153,0.12),transparent_55%)] pointer-events-none" />

      {/* Floating UI chrome — like a window/app frame */}
      <div className="absolute top-3 left-3 flex gap-1.5 z-10">
        <span className="w-2.5 h-2.5 rounded-full bg-rose-400/60" />
        <span className="w-2.5 h-2.5 rounded-full bg-violet-400/60" />
        <span className="w-2.5 h-2.5 rounded-full bg-violet-400/60" />
      </div>
      <div className="absolute top-3 right-3 text-white/60 text-[10px] font-mono uppercase tracking-wider z-10">lumora.studio</div>

      {/* Stage content */}
      <AnimatePresence mode="wait">
        <CurrentStage key={stage} />
      </AnimatePresence>

      {/* Stage progress dots */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 z-10" data-testid="hero-demo-dots">
        {STAGES.map((Stage, i) => (
          <span
            key={Stage.name || i}
            className={`h-1 rounded-full transition-all ${i === stage ? "w-6 bg-white" : "w-1.5 bg-white/30"}`}
            data-testid={i === stage ? "hero-demo-active-dot" : undefined}
          />
        ))}
      </div>

      <p className="absolute bottom-10 left-0 right-0 text-center text-white/65 text-[10px] uppercase tracking-[0.2em] z-10">
        Live demo · Hover to pause
      </p>
    </motion.div>
  );
}
